/**
 * `/orca/v3/runs/$runId` — one run, live. Registers the route eagerly with its guard,
 * its SSR seed and its stylesheets; the page itself is `$runId.lazy.tsx`, so nothing it
 * renders lands in the always-loaded chunk.
 *
 * The guard is `requireAuth`, never `requireAdmin`: a member reads their own runs. It
 * runs BEFORE the seed, so a signed-out visitor is redirected without the server ever
 * reading the run, and a signed-in one gets the run painted on first byte.
 *
 * @since 1.0.0
 */
import { createFileRoute } from "@tanstack/react-router";

import type { BeforeLoadArgs } from "~/lib/orca-ssr.ts";
import { requireAuth } from "~/lib/server-auth.ts";
import { v3Head } from "~/lib/v3-route.ts";
import { v3RunBeforeLoad } from "~/lib/v3-run-ssr.ts";

/**
 * The guard + seeder, extracted so it is unit-testable without the route machinery.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
export const v3RunPageBeforeLoad = async (args: BeforeLoadArgs) => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
  const auth = await requireAuth();
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- the SSR seeder returns a Promise
  const seeded = await v3RunBeforeLoad(args);
  return { auth, ...seeded };
};

export const Route = createFileRoute("/orca/v3/runs/$runId")({
  beforeLoad: v3RunPageBeforeLoad,
  head: v3Head,
});
